import React, { Component } from 'react'
import { Button, message } from 'antd'
import { injectIntl, FormattedMessage } from 'react-intl'
import { withApollo } from '@apollo/client/react/hoc'
import FormattedDuration from 'react-intl-formatted-duration'
import uniq from 'lodash/uniq'
import ActionsMenu from '../../components/ActionsMenu'
import {
	faTrash,
	faPlay,
	faPlus,
	faCheck,
	faCalendarTimes,
	faClipboard
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import gql from 'graphql-tag'
import { rrulestr } from 'rrule'

import config from '../../config'
import * as Auth from '../../auth'
import Table from '../../components/TableV2'
import { MessageProgramDetailsQuery } from './Queries'
import S from '../../components/Styles'
import BrowseMedia from './BrowseMedia'
import * as Utils from '../../components/Utils'
import { getAudioDuration } from '../../utils/audio'

class MediaListDisplay extends Component {
	state = {
		addMedia: false
	}

	mediaActions = (media, scheduled) => {
		const { messageProgram, intl } = this.props

		let actions = []
		actions.push({
			title: this.props.intl.formatMessage({ id: 'media.table.play' }),
			key: 'play',
			icon: faPlay,
			action: () => {
				try {
					if (this.audio) {
						this.audio.pause()
					}
					const url =
						config.cdn ?
							`${config.cdn.baseUrl}${config.cdn.paths.messagesOptimized}no-meta/stereo/${media._id}`
						:	`${config.aws.s3.baseUrl}${config.aws.s3.buckets.messagesOptimized}/no-meta/stereo/${media._id}`
					this.audio = new Audio(url)
					this.audio.play()
				} catch (e) {
					Utils.displayError(e)
				}
			}
		})
		if (Auth.hasAccess('messageProgram:edit', messageProgram.teamId)) {
			actions.push({
				title: this.props.intl.formatMessage({
					id: 'messagePrograms.details.removeFromList'
				}),
				key: 'deleteMedia',
				icon: faTrash,
				disabled: scheduled,
				variableMethod: () => ({
					id: messageProgram._id,
					mediaIds: messageProgram.mediaIds.filter(
						(mid) => mid !== media._id
					)
				}),
				mutation: {
					mutation: gql`
						mutation deleteMessageProgramMedia(
							$id: String!
							$mediaIds: [String]
						) {
							messageProgramUpdateById(
								_id: $id
								record: { mediaIds: $mediaIds }
							) {
								recordId
							}
						}
					`,
					refetchQueries: [
						{
							query: MessageProgramDetailsQuery,
							variables: { id: messageProgram._id }
						}
					]
				}
			})
		}

		// copy message ID action
		actions.push({
			title: this.props.intl.formatMessage({
				id: 'messagePrograms.details.copyMessageId'
			}),
			key: 'copyMessageId',
			icon: faClipboard,
			action: () => {
				navigator.clipboard
					.writeText(media._id)
					.then(() =>
						message.success(
							intl.formatMessage({
								id: 'messages.copiedToClipboard'
							})
						)
					)
					.catch(() =>
						message.error(intl.formatMessage({ id: 'error' }))
					)
			}
		})

		if (Auth.hasAccess('messageProgram:edit', messageProgram.teamId)) {
			actions.push({
				title: this.props.intl.formatMessage({
					id: 'messagePrograms.details.unschedule'
				}),
				key: 'unschedule',
				icon: faCalendarTimes,
				disabled: !scheduled,
				variableMethod: () => {
					let schedule = JSON.parse(messageProgram.schedule)
					schedule = schedule.filter((evt) => evt.media !== media._id)
					return {
						id: messageProgram._id,
						schedule: JSON.stringify(schedule)
					}
				},
				mutation: {
					mutation: gql`
						mutation updateMessageProgramSchedule(
							$id: String!
							$schedule: String
						) {
							messageProgramUpdateById(
								_id: $id
								record: { schedule: $schedule }
							) {
								recordId
							}
						}
					`,
					refetchQueries: [
						{
							query: MessageProgramDetailsQuery,
							variables: { id: messageProgram._id }
						}
					]
				}
			})
		}
		return actions
	}

	addMedia = async (media) => {
		const { messageProgram, client } = this.props

		try {
			const r = await client.mutate({
				variables: {
					id: messageProgram._id,
					mediaIds: uniq([...messageProgram.mediaIds, media._id])
				},
				mutation: gql`
					mutation updateMessageProgramAddMedia(
						$id: String!
						$mediaIds: [String]
					) {
						messageProgramUpdateById(
							_id: $id
							record: { mediaIds: $mediaIds }
						) {
							recordId
						}
					}
				`,
				refetchQueries: [
					{
						query: MessageProgramDetailsQuery,
						variables: { id: messageProgram._id }
					}
				]
			})
			Utils.parseMutationResponse(r)
		} catch (e) {
			Utils.displayError(e)
		}
	}

	// the "scheduled" state comes from the program schedule, not from the media
	// itself. We merge it into the records so TableV2 can sort on it and so the
	// rows are refreshed when the schedule changes.
	scheduledMediaIds = () => {
		const { messageProgram } = this.props

		let scheduledMedia = []
		if (messageProgram.schedule) {
			const events = JSON.parse(messageProgram.schedule)
			for (let e of events) {
				if (scheduledMedia.indexOf(e.media) < 0 && e.rule) {
					// only show as scheduled if scheduled in the future
					const rule = rrulestr(e.rule)
					const nextEvent = rule.after(new Date())
					if (nextEvent) {
						scheduledMedia.push(e.media)
					}
				}
			}
		}
		return scheduledMedia
	}

	getColumns = () => {
		const { intl, showActions } = this.props

		let columns = {
			duration: {
				name: intl.formatMessage({
					id: 'media.table.details.duration'
				}),
				accessor: (record) => getAudioDuration(record) ?? 0,
				render: (record) => (
					<FormattedDuration
						seconds={Math.floor(
							(getAudioDuration(record) ?? 0) / 1000
						)}
						textComponent="span"
						format="{hours} {minutes} {seconds}"
						unitDisplay="narrow"
					/>
				),
				type: 'Number'
			},
			scheduled: {
				name: intl.formatMessage({
					id: 'messagePrograms.details.scheduled'
				}),
				accessor: (record) => (record.scheduled ? 1 : 0),
				render: (record) =>
					record.scheduled ?
						<FontAwesomeIcon icon={faCheck} />
					:	null,
				type: 'Number'
			}
		}

		if (showActions) {
			columns.actions = {
				name: '',
				hiddenFromHeader: true,
				accessor: () => '',
				render: (record) => (
					<ActionsMenu
						size="small"
						menuItems={this.mediaActions(record, record.scheduled)}
					/>
				)
			}
		}

		return columns
	}

	render() {
		const { messageProgram, showActions } = this.props

		const scheduledMedia = this.scheduledMediaIds()
		const medias = (messageProgram.medias ?? [])
			.filter((media) => media !== null)
			.map((media) => ({
				...media,
				scheduled: scheduledMedia.indexOf(media._id) > -1
			}))

		let defaultColumns = ['name', 'duration', 'scheduled']
		if (showActions) {
			defaultColumns.push('actions')
		}

		return (
			<React.Fragment>
				<div
					style={{ ...S.horizontal, marginTop: 10, marginBottom: 10 }}
				>
					<Button onClick={() => this.setState({ addMedia: true })}>
						<FontAwesomeIcon icon={faPlus} />
						&nbsp;&nbsp;
						<FormattedMessage id="messagePrograms.details.addMedia" />
					</Button>
					<span style={{ marginLeft: 10 }}>
						<FormattedMessage id="messagePrograms.details.addMedia.info" />
					</span>
				</div>
				{/* no storageKey on purpose: the search query then lives in the
				    table state only, it is neither shared with the other tables
				    nor kept when leaving the message program */}
				<Table
					rowKey="_id"
					columns={this.getColumns()}
					defaultColumns={defaultColumns}
					data={medias}
					nameProp={(record) => record.name}
					searchProps={(record) => [record.name]}
					defaultSort="name"
					showSearch
				/>
				{this.state.addMedia && (
					<BrowseMedia
						selectMedia={(media) => media.isAudio}
						onClose={(media) => {
							if (media) {
								this.addMedia(media)
							}
							this.setState({ addMedia: false })
						}}
					/>
				)}
			</React.Fragment>
		)
	}
}

export default withApollo(injectIntl(MediaListDisplay))
